/**
 * Chunk Loading Recovery (P0-B)
 *
 * After a deploy, the active client may try to lazy-load a chunk hash that no
 * longer exists on the edge (Vite emits new hashes per build). The dynamic
 * import then rejects with "Failed to fetch dynamically imported module" and
 * the route/lazy boundary stays broken until the user refreshes.
 *
 * Strategy:
 *  - Listen for Vite's `vite:preloadError` event and matching unhandled
 *    rejections / errors with "dynamically imported module" in the message.
 *  - On first occurrence in a session, set a one-shot sessionStorage flag and
 *    `location.reload()`. The flag prevents reload loops if the failure is a
 *    genuine network problem rather than a stale build.
 */
const FLAG = "__vs_chunk_recovery_reloaded__";

function isChunkLoadError(msg: unknown): boolean {
  if (!msg) return false;
  const s = typeof msg === "string" ? msg : (msg as Error)?.message ?? String(msg);
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed/i.test(s);
}

function recover() {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(FLAG)) return; // already attempted this session
    sessionStorage.setItem(FLAG, String(Date.now()));
  } catch { /* private mode — still try reload once */ }
  try { window.location.reload(); } catch { /* noop */ }
}

export function initChunkRecovery() {
  if (typeof window === "undefined") return;
  // Vite >=5 dispatches this on the window for failed module preloads.
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault?.();
    recover();
  });
  window.addEventListener("error", (e) => {
    if (isChunkLoadError(e.message) || isChunkLoadError((e as ErrorEvent).error)) {
      recover();
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkLoadError((e as PromiseRejectionEvent).reason)) {
      recover();
    }
  });
  // Clear the flag once the page has stayed alive past hydration.
  window.setTimeout(() => {
    try { sessionStorage.removeItem(FLAG); } catch { /* noop */ }
  }, 10_000);
}
